import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { ErrorBoundary } from "@/app/ErrorBoundary";
import { Skeleton } from "@/components/ui/Skeleton";
import { getAccessToken } from "@/lib/api/client";
import { FilePreviewContext } from "@/features/library/useFilePreview";

// The PDF viewer is 640 kB and only a preview needs it, so it is fetched when
// the first document is opened rather than with the sign-in page.
const PdfViewer = lazy(() =>
  import("@/components/pdf/PdfViewer").then((module) => ({ default: module.PdfViewer })),
);

interface Loaded {
  url: string;
  type: string;
  name: string;
}

/**
 * Files are shown in the application, not handed to the browser.
 *
 * Opening a new tab was the obvious thing and it does not survive contact with
 * a real browser: window.open called after the bytes have been fetched is no
 * longer attributable to the click that asked for them, so Chrome treats it as
 * an unsolicited pop-up and blocks it without saying anything. The button did
 * nothing, and nothing is exactly what it looked like.
 *
 * So the file is fetched and drawn here. Both ways out of this panel, opening
 * a tab and saving, then happen inside a real click with the bytes already in
 * hand, which is the one arrangement no browser argues with.
 */
export function FilePreviewProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const current = useRef(0);

  const close = useCallback(() => {
    setTitle(null);
    setFailure(null);
    setLoaded((previous) => {
      if (previous) URL.revokeObjectURL(previous.url);
      return null;
    });
  }, []);

  const open = useCallback((path: string, label = "Document") => {
    const request = ++current.current;
    setTitle(label);
    setFailure(null);
    setLoaded(null);

    void (async () => {
      try {
        const response = await fetch(`/api/v1${path}`, {
          headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(
            response.status === 403
              ? "You do not have access to this file."
              : response.status === 404
                ? "This file is not available."
                : "This file could not be opened.",
          );
        }
        const blob = await response.blob();
        if (request !== current.current) return;
        setLoaded({
          url: URL.createObjectURL(blob),
          type: blob.type,
          name: path.split("/").filter(Boolean).slice(-2).join("-"),
        });
      } catch (error) {
        if (request !== current.current) return;
        setFailure(error instanceof Error ? error.message : "This file could not be opened.");
      }
    })();
  }, []);

  // Nothing holds the object URL once the panel is gone.
  useEffect(() => () => {
    if (loaded) URL.revokeObjectURL(loaded.url);
  }, [loaded]);

  return (
    <FilePreviewContext value={open}>
      {children}
      <Dialog
        open={title !== null}
        onOpenChange={(next) => !next && close()}
        title={title ?? ""}
        size="lg"
        footer={
          loaded ? (
            <>
              <Button
                variant="secondary"
                icon={<Download className="size-4" />}
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = loaded.url;
                  link.download = loaded.name || "document";
                  document.body.append(link);
                  link.click();
                  link.remove();
                }}
              >
                Save
              </Button>
              <Button
                variant="primary"
                icon={<ExternalLink className="size-4" />}
                onClick={() => {
                  // Inside the click, with the bytes already here. No blocker.
                  const tab = window.open(loaded.url, "_blank");
                  if (tab) tab.opener = null;
                  else toast.error("Your browser blocked the new tab. Save it instead.");
                }}
              >
                Open in a new tab
              </Button>
            </>
          ) : null
        }
      >
        {failure ? (
          <p className="py-6 text-center text-base text-fg-muted">{failure}</p>
        ) : !loaded ? (
          <Skeleton className="h-96 w-full" />
        ) : loaded.type === "application/pdf" ? (
          // Its own boundary: this provider sits above the router, so a
          // viewer that failed to arrive would otherwise blank the whole app.
          <ErrorBoundary title="The viewer could not load">
            <Suspense fallback={<Skeleton className="h-96 w-full" />}>
              <PdfViewer url={loaded.url} className="max-h-[70vh] overflow-y-auto" />
            </Suspense>
          </ErrorBoundary>
        ) : loaded.type.startsWith("image/") ? (
          <img src={loaded.url} alt={title ?? ""} className="mx-auto max-h-[70vh]" />
        ) : (
          <p className="py-6 text-center text-base text-fg-muted">
            This file cannot be shown here. Save it to open it.
          </p>
        )}
      </Dialog>
    </FilePreviewContext>
  );
}
